import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../context/AuthContext";
import api, { getApiDisplayLabel, getHealthCheckUrl } from "../../api/client";
import { checkApiHealth, parseApiError } from "../../api/apiErrors";
import {
  getMissingApiConfigMessage,
  getMixedContentWarning,
  resolveApiBaseURL,
} from "../../api/resolveApiBase";
import { getHomePathForRole } from "../../utils/rolePaths";
import { decodeRoleFromToken } from "../../utils/jwt";

export default function Login() {
  const { login, user, token, loading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [apiStatus, setApiStatus] = useState("checking");
  const [apiHint, setApiHint] = useState("");

  const runHealthCheck = useCallback(async () => {
    setApiStatus("checking");
    setApiHint("");

    const apiBase = resolveApiBaseURL();
    const missing = getMissingApiConfigMessage();
    const mixed = getMixedContentWarning(apiBase);

    const result = await checkApiHealth(getHealthCheckUrl(), {
      precheckMessage: mixed || missing || undefined,
      mixedContentMessage: mixed || undefined,
    });

    if (result.ok) {
      setApiStatus("ok");
      setApiHint("");
    } else {
      setApiStatus("error");
      setApiHint(result.message);
    }
  }, []);

  useEffect(() => {
    runHealthCheck();
  }, [runHealthCheck]);

  useEffect(() => {
    if (!loading && token && user?.role) {
      navigate(getHomePathForRole(user.role), { replace: true });
    }
  }, [loading, token, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const apiBase = resolveApiBaseURL();
      const health = await checkApiHealth(getHealthCheckUrl(), {
        precheckMessage: getMixedContentWarning(apiBase) || getMissingApiConfigMessage() || undefined,
      });
      if (!health.ok) {
        setApiStatus("error");
        setApiHint(health.message);
        throw new Error(health.message);
      }
      setApiStatus("ok");
      const loggedIn = await login(email, password);
      const roleFromJwt = decodeRoleFromToken(typeof localStorage !== "undefined" ? localStorage.getItem("token") : null);
      const role = loggedIn?.role || roleFromJwt || user?.role;
      navigate(getHomePathForRole(role), { replace: true });
    } catch (err) {
      setError(parseApiError(err, { apiBase: resolveApiBaseURL() }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBootstrap = async () => {
    setError("");
    setBootstrapping(true);
    try {
      const { data } = await api.post("/api/auth/register-bootstrap", {
        name: "Administrator",
        email: email || "admin@itsinception.com",
        password: password || "Jasraman@8900",
      });
      localStorage.setItem("token", data.token);
      try {
        localStorage.setItem("adCreativeAuthToken", data.token);
      } catch {
        /* ignore */
      }
      window.location.assign(getHomePathForRole(data.user?.role));
    } catch (err) {
      setError(parseApiError(err, { apiBase: resolveApiBaseURL() }));
    } finally {
      setBootstrapping(false);
    }
  };

  const apiLabel = getApiDisplayLabel();

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ background: "#101820" }}
    >
      <Paper sx={{ p: 4, width: 420, maxWidth: "92vw" }}>
        <Link component={RouterLink} to="/" variant="body2" sx={{ display: "block", mb: 2 }}>
          ← Back to website
        </Link>
        <Typography variant="h4" gutterBottom>
          Sign in
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Use your admin or designer account. First-time setup: create the first admin if the database is empty.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          API: {apiLabel}
          {apiStatus === "checking" ? " · checking…" : apiStatus === "ok" ? " · connected" : " · offline"}
        </Typography>

        {apiStatus === "error" ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={runHealthCheck}>
                Retry
              </Button>
            }
          >
            {apiHint || "API is not reachable. Start MongoDB, then run: npm run server (or npm run dev from project root)."}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={submitting || apiStatus === "checking"}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {submitting ? "Signing in…" : "Login"}
          </Button>
        </form>
        <Box mt={2}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleBootstrap}
            disabled={bootstrapping || apiStatus === "error"}
          >
            {bootstrapping ? "Creating admin…" : "Create first admin (only when no users exist)"}
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
